// src/pages/DebtStudio/components/Portfolios/index.jsx
import React, { useState, useCallback, useMemo, useEffect } from "react";
import { useProfile } from "context/ProfileContext";
import { useAppAccess } from "context/AppAccessContext";
import { supabase } from "services/supabaseClient";
import { useDialog } from "context/DialogContext";
import {
  getDebtStudioPermissions,
  normalizeSharing,
} from "lib/permissions";

import Page from "components/Page";
import FileRibbon from "components/FileManager/FileRibbon";
import FileManagerModal from "components/FileManager/FileManagerModal";
import FileManagerPanel from "components/FileManager/FileManagerPanel";
import TopTabMenu from "components/TopTabMenu";
import { ALL_DIMENSION_VALUES } from "components/DimensionValueFilter";

import { usePortfolioSession } from "pages/DebtStudio/context/PortfolioSessionContext";

import PortfoliosMain from "./Main";
import PortfoliosDebtService from "./DebtService";
import PortfoliosAnalysis from "./Analysis";

import { usePortfoliosFileManager, portfoliosOpenView } from "./fileManager";
import exportDebtPortfolioPdf from "pages/DebtStudio/reports/exportDebtPortfolioPdf";

function safeText(x) {
  return typeof x === "string" ? x : "";
}

function safeDateISO(x) {
  return typeof x === "string" ? x : "";
}

const PORTFOLIO_REPORT_MEMBER_SELECT = `
  id,
  ds_deal_id,
  include,
  sort_order,
  created_at,
  ds_deals:ds_deal_id (
    *,
    issuers:issuer_id (
      legal_name
    )
  )
`;

export default function DebtStudioPortfolios() {
  const {
    profile,
    loading: profileLoading,
    availableOrgs,
    selectedOrg,
    orgMode,
    selectOrg,
  } = useProfile();

  const { apps, loading: appsLoading } = useAppAccess();

  const appList = Array.isArray(apps) ? apps : [];
  const hasAccess = !!(profile?.is_admin || appList.includes("debt-studio"));

  const pfFM = usePortfoliosFileManager({ limit: 100 });

  const { confirm, alert } = useDialog();

  // Local-only UI state
  const [showOpenModal, setShowOpenModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);
  const [issueFilter, setIssueFilter] = useState(ALL_DIMENSION_VALUES);

  // Persisted session state (survives route navigation because provider is above Layout)
  const {
    portfolioId,
    setPortfolioId,
    activeTab,
    setActiveTab,
    viewState,
    setViewState,
    currentOwner,
    resetPortfolioSession,
  } = usePortfolioSession();

  const portfolio = viewState?.portfolio ?? null;
  const draftName = viewState?.draftName ?? "";
  const draftDescription = viewState?.draftDescription ?? "";
  const draftPeriodEndDate = viewState?.draftPeriodEndDate ?? "";
  const draftSharing = normalizeSharing(viewState?.draftSharing);
  const membersDirty = !!viewState?.membersDirty;

  const hasActivePortfolio = !!portfolio?.id;

  useEffect(() => {
    setIssueFilter(ALL_DIMENSION_VALUES);
  }, [portfolioId]);

  const tenantId = useMemo(() => {
    return selectedOrg?.tenant_id || profile?.tenant_id || null;
  }, [selectedOrg?.tenant_id, profile?.tenant_id]);

  const isOwner = useMemo(() => {
    if (!portfolio?.id || !profile?.id) return false;
    return portfolio.created_by === profile.id;
  }, [portfolio?.id, portfolio?.created_by, profile?.id]);

  const portfolioPermissions = useMemo(() => {
    return getDebtStudioPermissions(portfolio || {}, { id: profile?.id });
  }, [portfolio, profile?.id]);

  const canEditPortfolio = !!portfolio?.id && portfolioPermissions.canEdit;
  const canChangeSharing = !!portfolio?.id && portfolioPermissions.canChangeSharing;

  // Match DS Issues TopTabMenu contract (object tabs w/ key + label)
  const topTabs = useMemo(
    () => [
      { key: "Main", label: "Main" },
      { key: "Debt Service", label: "Debt Service" },
      { key: "Analysis", label: "Analysis" },
    ],
    []
  );

  // If org changes (or becomes unavailable), clear any session that is tied to a different org
  useEffect(() => {
    const orgId = selectedOrg?.id || null;

    if (!orgId) {
      if (portfolioId || portfolio?.id) resetPortfolioSession?.();
      return;
    }

    if (portfolio?.org_id && portfolio.org_id !== orgId) {
      resetPortfolioSession?.();
    }
  }, [
    selectedOrg?.id,
    portfolio?.org_id,
    portfolio?.id,
    portfolioId,
    resetPortfolioSession,
  ]);

  // Track meta-level changes including period_end
  const metaDirty = useMemo(() => {
    if (!portfolio?.id) return false;

    const baseName = safeText(portfolio?.name).trim();
    const baseDesc = safeText(portfolio?.description).trim();
    const basePeriodEnd = safeDateISO(portfolio?.period_end);
    const baseSharing = normalizeSharing(portfolio?.sharing);

    const draftN = safeText(draftName).trim();
    const draftD = safeText(draftDescription).trim();
    const draftP = safeDateISO(draftPeriodEndDate);
    const draftS = normalizeSharing(draftSharing);

    return (
      baseName !== draftN ||
      baseDesc !== draftD ||
      basePeriodEnd !== draftP ||
      baseSharing !== draftS
    );
  }, [
    portfolio?.id,
    portfolio?.name,
    portfolio?.description,
    portfolio?.period_end,
    portfolio?.sharing,
    draftName,
    draftDescription,
    draftPeriodEndDate,
    draftSharing,
  ]);

  const isDirtyAny = metaDirty || membersDirty;

  const handleTopTabChange = useCallback(
    (nextTab) => {
      if (nextTab === activeTab) return;
      setActiveTab(nextTab);
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    [activeTab, setActiveTab]
  );

  const loadPortfolioById = useCallback(
    async (nextPortfolioId) => {
      if (!nextPortfolioId || !selectedOrg?.id) return;

      const { data, error } = await supabase
        .from("ds_portfolios")
        .select("*")
        .eq("id", nextPortfolioId)
        .eq("org_id", selectedOrg.id)
        .single();

      if (error || !data) {
        await alert("Unable to load portfolio.", {
          title: "Load failed",
          okLabel: "OK",
        });
        return;
      }

      setPortfolioId(data.id);
      setViewState((prev) => ({
        ...(prev || {}),
        portfolio: data,
        draftName: data.name || "",
        draftDescription: data.description || "",
        draftPeriodEndDate: data.period_end || "",
        draftSharing: normalizeSharing(data.sharing),
        membersDirty: false,
      }));

      pfFM.openRecord?.(data);
      setActiveTab("Main");
    },
    [selectedOrg?.id, pfFM, setActiveTab, setPortfolioId, setViewState, alert]
  );

  const handleNew = useCallback(async () => {
    if (!selectedOrg?.id) {
      await alert("Select an organization first.", {
        title: "No organization",
        okLabel: "OK",
      });
      return;
    }
    if (!tenantId) {
      await alert("Tenant not resolved. Please reload and try again.", {
        title: "Tenant missing",
        okLabel: "OK",
      });
      return;
    }

    const defaultName = `New Portfolio ${new Date().toLocaleDateString()}`;
    const defaultPeriodEnd = new Date().toISOString().slice(0, 10); // YYYY-MM-DD

    setSaving(true);
    try {
      const { data, error } = await supabase
        .from("ds_portfolios")
        .insert([
          {
            tenant_id: tenantId,
            org_id: selectedOrg.id,
            name: defaultName,
            description: "",
            period_end: defaultPeriodEnd,
            sharing: "user",
          },
        ])
        .select("*")
        .single();

      if (error || !data) throw error || new Error("Create failed.");

      setPortfolioId(data.id);
      setViewState((prev) => ({
        ...(prev || {}),
        portfolio: data,
        draftName: data.name || "",
        draftDescription: data.description || "",
        draftPeriodEndDate: data.period_end || "",
        draftSharing: normalizeSharing(data.sharing),
        membersDirty: false,
      }));

      pfFM.openRecord?.(data);
      pfFM.refresh?.();

      setActiveTab("Main");
    } catch (e) {
      console.error("❌ [Portfolios] create failed:", e);
      await alert(e?.message || "Failed to create portfolio.", {
        title: "Create failed",
        okLabel: "OK",
      });
    } finally {
      setSaving(false);
    }
  }, [
    selectedOrg?.id,
    tenantId,
    pfFM,
    setActiveTab,
    setPortfolioId,
    setViewState,
    alert,
  ]);

  const handleSave = useCallback(async () => {
    if (!portfolio?.id) return;
    if (!canEditPortfolio) return;

    const name = safeText(draftName).trim();
    if (!name) {
      await alert("Portfolio name is required.", {
        title: "Missing name",
        okLabel: "OK",
      });
      return;
    }

    const period_end = safeDateISO(draftPeriodEndDate);
    if (!period_end) {
      await alert("Period End date is required.", {
        title: "Missing Period End",
        okLabel: "OK",
      });
      return;
    }

    setSaving(true);
    try {
      const { data, error } = await supabase
        .from("ds_portfolios")
        .update({
          name,
          description: safeText(draftDescription).trim() || null,
          period_end,
          sharing: canChangeSharing
            ? normalizeSharing(draftSharing)
            : normalizeSharing(portfolio?.sharing),
          updated_at: new Date().toISOString(),
        })
        .eq("id", portfolio.id)
        .eq("org_id", selectedOrg?.id)
        .select("*")
        .single();

      if (error || !data) throw error || new Error("Save failed.");

      setPortfolioId(data.id);
      setViewState((prev) => ({
        ...(prev || {}),
        portfolio: data,
        draftName: data.name || "",
        draftDescription: data.description || "",
        draftPeriodEndDate: data.period_end || "",
        draftSharing: normalizeSharing(data.sharing),
        membersDirty: false,
      }));

      pfFM.refresh?.();
      pfFM.openRecord?.(data);
    } catch (e) {
      console.error("❌ [Portfolios] save failed:", e);
      await alert(e?.message || "Failed to save portfolio.", {
        title: "Save failed",
        okLabel: "OK",
      });
    } finally {
      setSaving(false);
    }
  }, [
    portfolio?.id,
    selectedOrg?.id,
    canEditPortfolio,
    canChangeSharing,
    draftName,
    draftDescription,
    draftPeriodEndDate,
    draftSharing,
    portfolio?.sharing,
    pfFM,
    setPortfolioId,
    setViewState,
    alert,
  ]);

  const handleDelete = useCallback(async () => {
    if (!portfolio?.id) return;
    if (!isOwner) return;

    const ok = await confirm("Delete this portfolio? This cannot be undone.", {
      title: "Delete portfolio",
      okLabel: "Delete",
      cancelLabel: "Cancel",
    });
    if (!ok) return;

    try {
      await pfFM.deleteRecord?.(portfolio.id);
      resetPortfolioSession?.();
      setActiveTab("Main");
      await alert("Portfolio deleted.", { title: "Deleted", okLabel: "OK" });
    } catch (e) {
      await alert(e?.message || "Failed to delete portfolio.", {
        title: "Delete failed",
        okLabel: "OK",
      });
    }
  }, [
    portfolio?.id,
    isOwner,
    pfFM,
    resetPortfolioSession,
    setActiveTab,
    confirm,
    alert,
  ]);

  const handleExportPdf = useCallback(async () => {
    if (!portfolio?.id || !selectedOrg?.id) return;

    setIsPdfGenerating(true);

    try {
      const { data, error } = await supabase
        .from("ds_portfolio_issues")
        .select(PORTFOLIO_REPORT_MEMBER_SELECT)
        .eq("org_id", selectedOrg.id)
        .eq("portfolio_id", portfolio.id)
        .order("sort_order", { ascending: true, nullsFirst: false })
        .order("created_at", { ascending: true });

      if (error) throw error;

      await exportDebtPortfolioPdf({
        orgId: selectedOrg.id,
        portfolio: {
          ...portfolio,
          name: safeText(draftName).trim() || portfolio.name,
          description: safeText(draftDescription).trim() || "",
          period_end: safeDateISO(draftPeriodEndDate) || portfolio.period_end,
        },
        members: Array.isArray(data) ? data : [],
        orgName: selectedOrg?.name || "",
        periodEndDate: safeDateISO(draftPeriodEndDate) || portfolio.period_end,
      });
    } catch (e) {
      console.error("❌ [Portfolios] PDF export failed:", e);
      await alert(e?.message || "Failed to export portfolio PDF.", {
        title: "PDF export failed",
        okLabel: "OK",
      });
    } finally {
      setIsPdfGenerating(false);
    }
  }, [
    portfolio,
    selectedOrg?.id,
    selectedOrg?.name,
    draftName,
    draftDescription,
    draftPeriodEndDate,
    alert,
  ]);

  // Dirty-triggering wrappers (so Main tab doesn't need to know about dirty rules)
  const setNameDirty = useCallback(
    (v) => setViewState((prev) => ({ ...(prev || {}), draftName: v })),
    [setViewState]
  );
  const setDescriptionDirty = useCallback(
    (v) => setViewState((prev) => ({ ...(prev || {}), draftDescription: v })),
    [setViewState]
  );
  const setSharingDirty = useCallback(
    (v) =>
      setViewState((prev) => ({
        ...(prev || {}),
        draftSharing: normalizeSharing(v),
      })),
    [setViewState]
  );
  const setPeriodEndDirty = useCallback(
    (v) => setViewState((prev) => ({ ...(prev || {}), draftPeriodEndDate: v })),
    [setViewState]
  );

  const markMembersDirty = useCallback(() => {
    setViewState((prev) => ({ ...(prev || {}), membersDirty: true }));
  }, [setViewState]);

  if (profileLoading || appsLoading) {
    return <div>Loading user profile…</div>;
  }

  if (!hasAccess) {
    return (
      <Page title="DebtStudio - Portfolios">
        <div className="p-6 text-muted">You do not have access to this app.</div>
      </Page>
    );
  }

  // Match DS Issues behavior: "Open" is only available once a workspace exists
  const showWorkspace = hasActivePortfolio;

  return (
    <Page
      title="DebtStudio - Portfolios"
      showOrganizationSelector
      ribbon={
        <FileRibbon
          entityName="Portfolio"
          fileManager={{
            availableOrgs,
            selectedOrg,
            orgMode,
            selectOrg,
          }}
          onNew={handleNew}
          onSave={
            canEditPortfolio && hasActivePortfolio && isDirtyAny ? handleSave : null
          }
          onSaveAs={null}
          onCopy={null}
          onOpenBrowse={showWorkspace ? () => setShowOpenModal(true) : null}
          onDelete={isOwner && hasActivePortfolio ? handleDelete : null}
          currentIssuer={null}
          currentName={portfolio?.name || null}
          currentOwner={currentOwner}
          animateSaving
          currentStatus={
            !hasActivePortfolio
              ? "unsaved"
              : saving
              ? "saving"
              : isDirtyAny
              ? "modified"
              : "saved"
          }
          isReadOnly={!canEditPortfolio && hasActivePortfolio}
        >
          {!showWorkspace ? (
            !selectedOrg ? (
              <div className="text-sm text-muted">
                Select an organization above to browse portfolios.
              </div>
            ) : (
              <FileManagerPanel
                entityName="Portfolio"
                adapter={pfFM}
                view={portfoliosOpenView}
                onSelect={(row) => loadPortfolioById(row.id)}
                showToolbar
                showFooter
                maxHeightClassName="max-h-[55vh]"
              />
            )
          ) : null}
        </FileRibbon>
      }
    >
      {hasActivePortfolio && (
        <div className="rounded-2xl border border-border bg-app shadow-sm">
          <TopTabMenu
            tabs={topTabs}
            activeTab={activeTab}
            onChange={handleTopTabChange}
          />

          <div className="p-6 border-t border-border rounded-b-2xl">
            {activeTab === "Main" && (
              <PortfoliosMain
                portfolio={portfolio}
                draftName={draftName}
                draftDescription={draftDescription}
                setDraftName={setNameDirty}
                setDraftDescription={setDescriptionDirty}
                draftSharing={draftSharing}
                setDraftSharing={setSharingDirty}
                draftPeriodEndDate={draftPeriodEndDate}
                setDraftPeriodEndDate={setPeriodEndDirty}
                canEdit={canEditPortfolio}
                canChangeSharing={canChangeSharing}
                saving={saving}
                onSave={handleSave}
                markMembersDirty={markMembersDirty}
              />
            )}

            {activeTab === "Debt Service" && (
              <PortfoliosDebtService
                portfolio={portfolio}
                periodEndDate={draftPeriodEndDate}
                onExportPdf={handleExportPdf}
                isPdfGenerating={isPdfGenerating}
                issueFilter={issueFilter}
                onIssueFilterChange={setIssueFilter}
              />
            )}

            {activeTab === "Analysis" && (
              <PortfoliosAnalysis
                portfolio={portfolio}
                onExportPdf={handleExportPdf}
                isPdfGenerating={isPdfGenerating}
                issueFilter={issueFilter}
                onIssueFilterChange={setIssueFilter}
              />
            )}
          </div>
        </div>
      )}

      {showOpenModal && selectedOrg ? (
        <FileManagerModal
          entityName="Portfolio"
          adapter={pfFM}
          view={portfoliosOpenView}
          onClose={() => setShowOpenModal(false)}
          onSelect={(row) => {
            setShowOpenModal(false);
            loadPortfolioById(row.id);
          }}
        />
      ) : null}
    </Page>
  );
}
